import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeaders, handleApiError } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Card } from '../ui/card';
import {
  Plus, Building, Home, MapPin, Trash2, Pencil, ArrowRight,
  Wallet, TrendingUp, TrendingDown, Users, Banknote, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import PropertyDetail from './PropertyDetail';

const PROPERTY_TYPES = [
  { value: 'commercial', label: 'Commercial', icon: Building, color: 'blue' },
  { value: 'residential', label: 'Residential', icon: Home, color: 'green' },
  { value: 'plot', label: 'Plot/Land', icon: MapPin, color: 'amber' },
];

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const PropertyTab = () => {
  const [properties, setProperties] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    name: '',
    address: '',
    type: 'commercial',
    value: '',
    notes: '',
  });

  const fetchAll = useCallback(async () => {
    try {
      const [propsRes, sumRes] = await Promise.all([
        axios.get(`${API}/properties`, { headers: getAuthHeaders() }),
        axios.get(`${API}/properties/dashboard/summary`, { headers: getAuthHeaders() }),
      ]);
      setProperties(propsRes.data || []);
      setSummary(sumRes.data || null);
    } catch (err) {
      toast.error(handleApiError(err, 'Failed to load properties'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const resetForm = () => {
    setForm({ name: '', address: '', type: 'commercial', value: '', notes: '' });
    setEditing(null);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '',
      address: p.address || '',
      type: p.type || 'commercial',
      value: p.value || '',
      notes: p.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Property name is required');
      return;
    }
    const payload = {
      name: form.name.trim(),
      address: form.address || null,
      type: form.type,
      value: parseFloat(form.value) || 0,
      notes: form.notes || null,
    };
    try {
      if (editing) {
        await axios.put(`${API}/properties/${editing.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Property updated');
      } else {
        await axios.post(`${API}/properties`, payload, { headers: getAuthHeaders() });
        toast.success('Property added');
      }
      setDialogOpen(false);
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(handleApiError(err, 'Failed to save property'));
    }
  };

  const handleDelete = async (p) => {
    const password = window.prompt(
      `⚠️  Delete "${p.name}"?\n\nThis will permanently remove ALL linked partners, expenses, rents, loans, EMI history, and partner payments.\n\nEnter the property-delete password to confirm:`
    );
    if (password === null) return; // cancelled
    if (!password) {
      toast.error('Password is required');
      return;
    }
    try {
      await axios.post(
        `${API}/properties/${p.id}/delete`,
        { password },
        { headers: getAuthHeaders() }
      );
      toast.success('Property deleted');
      fetchAll();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error('Incorrect password — property not deleted');
      } else {
        toast.error(handleApiError(err, 'Failed to delete property'));
      }
    }
  };

  if (selectedId) {
    return (
      <PropertyDetail
        propertyId={selectedId}
        onBack={() => {
          setSelectedId(null);
          fetchAll();
        }}
      />
    );
  }

  if (loading) {
    return <div className="text-slate-500 py-12 text-center">Loading properties...</div>;
  }

  return (
    <div className="space-y-6" data-testid="property-tab-root">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-heading tracking-tight text-slate-900">Property Management</h2>
          <p className="text-sm text-slate-500">
            Track properties, partners, rental income, expenses & loans
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="add-property-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Property
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          label="Properties"
          value={summary?.total_properties || 0}
          sub={`${formatCurrency(summary?.total_value || 0)} total value`}
          icon={Building}
          tone="blue"
        />
        <SummaryCard
          label="Rent Received"
          value={formatCurrency(summary?.total_rent_received || 0)}
          sub={`Pending: ${formatCurrency(summary?.total_rent_pending || 0)}`}
          icon={TrendingUp}
          tone="emerald"
        />
        <SummaryCard
          label="Property Expenses"
          value={formatCurrency(summary?.total_expenses || 0)}
          sub="Cumulative"
          icon={TrendingDown}
          tone="rose"
        />
        <SummaryCard
          label="Loan Balance"
          value={formatCurrency(summary?.total_loan_balance || 0)}
          sub={`${summary?.partners_count || 0} partners · ${formatCurrency(
            summary?.total_partner_investment || 0
          )} invested`}
          icon={Banknote}
          tone="amber"
        />
      </div>

      {/* Property Cards */}
      {properties.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Building className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 mb-4">No properties added yet.</p>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" /> Add First Property
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {properties.map((p) => {
            const t = PROPERTY_TYPES.find((x) => x.value === p.type) || PROPERTY_TYPES[0];
            const Icon = t.icon;
            return (
              <Card
                key={p.id}
                className="p-5 border-slate-200 hover:shadow-md hover:border-emerald-300 transition cursor-pointer group"
                onClick={() => setSelectedId(p.id)}
                data-testid={`property-card-${p.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-11 h-11 rounded-lg flex items-center justify-center bg-${t.color}-50 text-${t.color}-600`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{p.name}</h3>
                      <p className="text-xs text-slate-500 truncate">
                        {p.address || 'No address'}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs">
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 capitalize">
                          {p.type}
                        </span>
                        <span className="text-slate-700 font-medium">
                          {formatCurrency(p.value || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(p);
                      }}
                      data-testid={`edit-property-${p.id}`}
                    >
                      <Pencil className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(p);
                      }}
                      data-testid={`delete-property-${p.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-3 text-xs text-emerald-600 font-medium opacity-70 group-hover:opacity-100">
                  Open ledger <ArrowRight className="h-3 w-3 ml-1" />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) resetForm();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Property' : 'Add Property'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Property Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Shop No. 5, Main Market"
                required
                data-testid="property-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Street, City"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={form.type}
                  onValueChange={(v) => setForm({ ...form, type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estimated Value (₹)</Label>
                <Input
                  type="number"
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="submit-property-btn"
              >
                {editing ? 'Save Changes' : 'Add Property'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SummaryCard = ({ label, value, sub, icon: Icon, tone }) => {
  const tones = {
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    rose: 'bg-rose-50 text-rose-600',
    amber: 'bg-amber-50 text-amber-600',
  };
  return (
    <Card className="p-4 border-slate-200">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-sm font-medium text-slate-600">{label}</span>
      </div>
      <p className="text-2xl font-heading tracking-tight text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1 truncate">{sub}</p>}
    </Card>
  );
};

export default PropertyTab;
